import InputSend from '@components/Inputs/InputSend'
import React from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { LucaNodeConfig } from '../nodes/types'
import { LucaApprovedToolsPicker } from './LucaApprovedToolsPicker'
import { useWorkflowCatalogs } from './WorkflowCatalogsContext'
import { useWorkflowConfigForm } from './useWorkflowConfigForm'

const INSTRUCTIONS_MAX_LENGTH = 16_000

interface Props {
  nodeId: string
  config: LucaNodeConfig
  onChange: (config: LucaNodeConfig) => void
}

export const LucaConfigForm: React.FC<Props> = ({ nodeId, config, onChange }) => {
  const [t] = useTranslation()
  const {
    lucaTools,
    lucaToolsQuery: { isLoading, isError, isSuccess, refetch }
  } = useWorkflowCatalogs()
  const { control, pushConfig } = useWorkflowConfigForm<LucaNodeConfig>({
    defaultValues: {
      instructions: config.instructions,
      approved_tools: config.approved_tools ?? []
    },
    onChange
  })

  return (
    <>
      <Controller
        name="instructions"
        control={control}
        rules={{
          required: t('workflows.config.luca.instructionsRequired'),
          maxLength: {
            value: INSTRUCTIONS_MAX_LENGTH,
            message: t('workflows.config.luca.instructionsTooLong', {
              max: INSTRUCTIONS_MAX_LENGTH
            })
          }
        }}
        render={({ field, fieldState }) => (
          <FieldStack>
            <FieldLabel>{t('workflows.config.luca.instructions')}</FieldLabel>
            <InputSend
              onBlur={field.onBlur}
              onChangeController={value => {
                field.onChange(value)
                pushConfig()
              }}
              ref={field.ref}
              actionElement={<span />}
              name={`workflow-luca-instructions-${nodeId}`}
              value={field.value}
              error={fieldState.error}
              minRows={4}
              maxRows={10}
            />
            <HelperText>{t('workflows.config.luca.hint')}</HelperText>
          </FieldStack>
        )}
      />
      <Controller
        name="approved_tools"
        control={control}
        render={({ field }) => (
          <LucaApprovedToolsPicker
            nodeId={nodeId}
            selected={field.value ?? []}
            catalogTools={lucaTools}
            showToolsLoading={isLoading}
            showToolsError={isError}
            toolsLoaded={isSuccess}
            onCommit={nextSelected => {
              field.onChange(nextSelected)
              pushConfig()
            }}
            onRetry={() => {
              void refetch()
            }}
          />
        )}
      />
    </>
  )
}

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.metalGray};
`

const HelperText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  line-height: 1.4;
`
